"use client";

/**
 * Divider Renderer
 * Adjustable vertical spacing between sections, with an optional background fill and an optional
 * visible horizontal rule through the middle. Not rendered through `SectionContainer`, which adds
 * fixed padding and title chrome that would fight the divider's own exact pixel height.
 *
 * Hidden from assistive tech while it's only spacing; once the rule is shown it carries an `<hr>`
 * exposed as a `separator`, because at that point it is a division a sighted reader can see.
 */

import type React from "react";
import { useMemo } from "react";
import { useBuilderContext } from "../../../_hooks/useBuilderContext";
import { getBackgroundStyle, toCssHex } from "../../../_logic/background-styles";
import { isValidHexColor } from "@/features/landing-pages/_logic/color-utils";
import {
  DEFAULT_DIVIDER_HEIGHT_PX,
  DEFAULT_DIVIDER_LINE_COLOR,
  DEFAULT_DIVIDER_LINE_THICKNESS_PX,
  DIVIDER_BG_IMAGE_RENDER_WIDTH,
  DIVIDER_LINE_STYLE_VALUES,
  MAX_DIVIDER_HEIGHT_PX,
  MAX_DIVIDER_LINE_THICKNESS_PX,
  MIN_DIVIDER_HEIGHT_PX,
  MIN_DIVIDER_LINE_THICKNESS_PX,
  SECTION_BG_IMAGE_RENDER_QUALITY,
} from "../../../_logic/constants";
import { transformStorageImageUrl } from "../../../_logic/image-cdn";
import { isValidStorageUrl } from "../../../_logic/utils";
import type { DividerComponent } from "../../../_types";

interface DividerRendererProps {
  component: DividerComponent;
}

/**
 * Renders a Divider section: adjustable vertical space, an optional background fill, and an
 * optional horizontal rule through the middle.
 *
 * Every value that reaches the inline `style` here is validated or clamped first, and stays that
 * way even though two other layers now check them too — the copilot's field spec on write, and
 * `validateDividerStyle` at save time. React doesn't escape `;` in a style value during SSR, so an
 * unvalidated one is a CSS injection on the published page rather than just a bad-looking divider;
 * this is the last layer before that happens, and it renders whatever is already stored, including
 * rows written before either of the other two checks existed.
 * @param component - The divider component to render; every style field on it is treated as untrusted.
 * @returns The rendered divider section.
 */
export function DividerRenderer({ component }: DividerRendererProps): React.ReactElement {
  const { height, showLine, lineColor, lineThickness, lineStyle, background } = component;
  const { primaryColor, backgroundColor } = useBuilderContext();

  // Clamps against the editor's bounds so a malformed/out-of-range persisted value (e.g. from
  // direct API edits) can't render an absurdly tall or negative-height spacer.
  //
  // Coerced and finite-checked first: `height` is typed `number` but arrives from stored JSON, and
  // `Math.max(8, "abc")` is NaN, which renders as the literal `NaNpx`. The fallback is the actual
  // default rather than the safety floor, so a divider whose height didn't persist matches what
  // the editor shows for it (medium) instead of collapsing to an 8px sliver the editor can't
  // explain.
  const clampedHeight = useMemo(() => {
    const raw = Number(height);
    const usable = Number.isFinite(raw) && raw > 0 ? raw : DEFAULT_DIVIDER_HEIGHT_PX;
    return Math.min(MAX_DIVIDER_HEIGHT_PX, Math.max(MIN_DIVIDER_HEIGHT_PX, usable));
  }, [height]);

  // Every one of these three lands in an inline `style` and all three are copilot-writable, so
  // each is validated here rather than trusted — last of three layers, after the field spec on
  // write and `validateDividerStyle` at save, and the only one that also covers rows stored
  // before those existed. React does not escape `;` in a style value during
  // SSR — `background-styles.ts` documents the same hazard for section backgrounds — so an
  // unvalidated string is a live CSS injection on the published page: a `lineColor` of
  // `red; background-image: url(https://evil/track.png)` serializes verbatim, and a
  // `lineThickness` of `1px; position: fixed; inset: 0; z-index: 9999` turns a decorative rule
  // into a full-screen overlay.
  const lineStyleProps = useMemo(() => {
    const thickness = Number(lineThickness ?? DEFAULT_DIVIDER_LINE_THICKNESS_PX);
    const safeThickness = Number.isFinite(thickness)
      ? Math.min(MAX_DIVIDER_LINE_THICKNESS_PX, Math.max(MIN_DIVIDER_LINE_THICKNESS_PX, thickness))
      : DEFAULT_DIVIDER_LINE_THICKNESS_PX;

    return {
      borderTopWidth: `${safeThickness}px`,
      // Membership test, so an unrecognised style falls back to a drawn rule instead of silently
      // dropping the one the organizer asked for.
      borderTopStyle: lineStyle && DIVIDER_LINE_STYLE_VALUES.has(lineStyle) ? lineStyle : "solid",
      // `toCssHex` as well as `isValidHexColor`: the validator's `#` is optional, so a bare
      // "e5e7eb" passes it and then renders as invalid CSS — the rule silently falls back to
      // `currentColor` rather than the requested or the default color. `getBackgroundStyle` pairs
      // these two for exactly this reason; validating without normalizing is only half of it.
      borderTopColor:
        lineColor && isValidHexColor(lineColor) ? toCssHex(lineColor) : toCssHex(DEFAULT_DIVIDER_LINE_COLOR),
    } as React.CSSProperties;
  }, [lineThickness, lineStyle, lineColor]);

  // Background fill for the spacer itself — same solid/image/gradient shape used by every other
  // section (see SectionContainer), minus the legibility scrim SectionContainer adds for its own
  // text content: a divider has none.
  const override = background && background.kind !== "none" ? background : undefined;
  // Memoized like `SectionContainer`'s equivalent: `getBackgroundStyle` returns a fresh object
  // every call, so recomputing it inline made it a new dependency identity on every render and
  // the `backgroundStyle` memo below it never actually held.
  const flatBackgroundStyle = useMemo(
    () => (override ? getBackgroundStyle(override, primaryColor) : null),
    [override, primaryColor]
  );
  // `isValidStorageUrl`, not `isSafeUrl`: the latter accepts any http(s) host, which would let a
  // divider background point at a third-party tracking origin. Every sibling section and the
  // server-side save validator (`validate-image-urls.ts`) gate this same field on our own storage
  // host, and the renderer has no business being the one loose check in that set.
  const safeImageUrl =
    override?.kind === "image" && override.url && isValidStorageUrl(override.url) ? override.url : null;
  const encodedImageUrl = useMemo(() => {
    if (!safeImageUrl) return null;
    const resolved = transformStorageImageUrl(safeImageUrl, {
      width: DIVIDER_BG_IMAGE_RENDER_WIDTH,
      quality: SECTION_BG_IMAGE_RENDER_QUALITY,
    });
    return encodeURI(resolved).replace(/['()]/g, (c) => `%${c.charCodeAt(0).toString(16).toUpperCase()}`);
  }, [safeImageUrl]);

  // With no override, paint the page's own background color explicitly — matching what the
  // editor tells the user ("matches your page background") and what every other section already
  // does (SectionContainer's `bg="white"` variant). Leaving this unset instead of falling back
  // rendered fully transparent, revealing whatever's behind the canvas (plain white) rather than
  // the page's actual background — "Reset to default" looked like it did nothing as a result.
  const backgroundStyle = useMemo<React.CSSProperties>(() => {
    if (encodedImageUrl) {
      return {
        backgroundImage: `url('${encodedImageUrl}')`,
        // Painted under the photo, as `SectionContainer` does: without it the divider is fully
        // transparent while the image loads and stays that way if it 404s, showing whatever is
        // behind the canvas instead of the page's own background — the same bug this renderer
        // already fixed for the no-override case, reintroduced under an image.
        backgroundColor,
        backgroundSize: "cover",
        backgroundPosition: "center",
      };
    }
    return flatBackgroundStyle ?? { backgroundColor };
  }, [encodedImageUrl, flatBackgroundStyle, backgroundColor]);

  return (
    <div
      className="flex w-full items-center justify-center px-4"
      style={{ height: `${clampedHeight}px`, ...backgroundStyle }}
      // Hidden from assistive tech only while it really is just empty space. With the rule turned
      // on it's a visible separator between sections — the one thing `<hr>`'s `separator` role
      // exists for — and blanket `aria-hidden` meant a screen reader got no hint of a division the
      // sighted reader can see. Pure spacing stays hidden: announcing it would be noise.
      aria-hidden={showLine ? undefined : "true"}
    >
      {showLine && <hr className="w-full max-w-6xl border-0 border-t" style={lineStyleProps} />}
    </div>
  );
}
